import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import BookCover from "@/components/BookCover";
import ChapterOnePlayer from "@/components/ChapterOnePlayer";
import { AMAZON_URL, PRINCIPLES, RECOGNITION_EXPERIENCES, SEO as SEO_CONTENT } from "@/content";
import BookTagline from "@/components/BookTagline";

const AmazonButton = ({ testid, primary = true }) => (
  <a
    href={AMAZON_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={
      primary
        ? "inline-flex items-center justify-center gap-2 bg-teal text-ivory text-xs uppercase tracking-[0.25em] px-10 py-4 hover:bg-deepteal transition-colors duration-500"
        : "inline-flex items-center justify-center gap-2 border border-stone text-charcoal text-xs uppercase tracking-[0.25em] px-10 py-4 hover:border-teal hover:text-deepteal transition-colors duration-500"
    }
  >
    Buy on Amazon
    <ArrowUpRight size={14} />
  </a>
);

const RegulatePage = () => (
  <>
    <SEO title={SEO_CONTENT.regulate.title} description={SEO_CONTENT.regulate.description} />

    <section data-testid="regulate-hero" className="relative overflow-hidden grain border-b border-stone/60">
      <div
        aria-hidden="true"
        className="absolute -top-32 right-0 w-[46vw] max-w-[560px] aspect-square rounded-full bg-[radial-gradient(circle,rgba(76,144,128,0.06),transparent_65%)] blur-3xl anim-orb"
      />
      <div className="relative mx-auto max-w-6xl px-6 md:px-10 pt-40 md:pt-52 pb-24 md:pb-32">
        <div className="grid md:grid-cols-12 gap-14 items-center">
          <Reveal className="md:col-span-5" y={36}>
            <div className="max-w-[330px] mx-auto md:max-w-none">
              <BookCover />
            </div>
          </Reveal>
          <div className="md:col-span-7 md:col-start-6">
            <Reveal>
              <p className="eyebrow mb-6">A book by Tara Aurora</p>
              <h1 className="font-serif text-6xl md:text-7xl lg:text-8xl tracking-[0.06em]" data-testid="regulate-title">
                REGULATE
              </h1>
              <p className="mt-6 font-serif italic text-xl md:text-2xl text-deepteal leading-relaxed" data-testid="regulate-tagline">
                <BookTagline />
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-9 text-base md:text-lg leading-relaxed text-taupe max-w-xl">
                REGULATE isn’t another book about becoming a better version of
                yourself. It’s an invitation to understand the version of you
                that learned to survive.
              </p>
              <p className="mt-5 text-base md:text-lg leading-relaxed text-taupe max-w-xl">
                Your body isn’t working against you. It’s trying to protect you —
                and when you learn its language, everything begins to change.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-11 flex flex-col sm:flex-row gap-4">
                <AmazonButton testid="regulate-hero-amazon-button" />
                <Link
                  to="/begin-here#regulate-intro"
                  data-testid="regulate-read-excerpt-button"
                  className="text-center border border-stone text-charcoal text-xs uppercase tracking-[0.25em] px-10 py-4 hover:border-teal hover:text-deepteal transition-colors duration-500"
                >
                  Read the Introduction
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    <section data-testid="chapter-one-section" className="bg-cream/60 border-b border-stone/60">
      <div className="mx-auto max-w-3xl px-6 md:px-10 py-24 md:py-32">
        <Reveal>
          <p className="eyebrow mb-6">Listen</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.1]" data-testid="chapter-one-heading">
            A First Listen to REGULATE
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 text-base md:text-lg leading-relaxed text-taupe">
            Step inside the opening pages of REGULATE.
          </p>
          <p className="mt-3 text-base md:text-lg leading-relaxed text-taupe">
            Listen to an excerpt from Chapter One and experience the voice,
            rhythm, and ideas behind the book.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-12 md:mt-14 border border-stone bg-ivory px-6 py-8 md:px-10 md:py-10">
            <ChapterOnePlayer />
          </div>
          <div className="mt-12 text-center">
            <p className="text-[11px] uppercase tracking-[0.3em] text-taupe">
              From REGULATE
            </p>
            <p className="mt-3 font-serif italic text-lg text-deepteal">
              Calm the Nervous System. Heal the Root. Live with Choice.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    <section data-testid="regulate-who-for" className="bg-white/50 border-b border-stone/60">
      <div className="mx-auto max-w-6xl px-6 md:px-10 py-24 md:py-36">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Reveal>
              <p className="eyebrow mb-6">Who it’s for</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.1]">
                For the ones holding everything together.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-base md:text-lg leading-relaxed text-taupe max-w-md">
                You’re functioning. You’re handling everything. You’re showing
                up. But underneath it all, your body feels like it’s always
                preparing for something.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <ul>
              {RECOGNITION_EXPERIENCES.map((item, i) => (
                <Reveal key={item} delay={0.05 * i} y={14}>
                  <li className="flex items-baseline gap-6 border-t border-stone/80 py-5 first:border-t-0 md:first:border-t">
                    <span className="h-1.5 w-1.5 rounded-full bg-teal shrink-0 translate-y-[-2px]" aria-hidden="true" />
                    <span className="font-serif text-xl md:text-2xl text-charcoal/90">{item}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section data-testid="regulate-discover" className="border-b border-stone/60">
      <div className="mx-auto max-w-6xl px-6 md:px-10 py-24 md:py-36">
        <Reveal className="max-w-2xl">
          <p className="eyebrow mb-6">Inside the book</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.1]">
            What you’ll discover.
          </h2>
          <p className="mt-8 text-base md:text-lg leading-relaxed text-taupe">
            Healing isn’t about fixing yourself. It’s about understanding what
            your system learned to do to keep you safe. REGULATE moves gently
            through four movements:
          </p>
        </Reveal>
        <div className="mt-16 md:mt-20">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.number} delay={0.06 * i} y={18}>
              <div className="grid md:grid-cols-12 gap-3 md:gap-6 items-baseline border-t border-stone/80 py-9 md:py-12">
                <span className="md:col-span-2 font-serif italic text-3xl text-teal/90">{p.number}</span>
                <h3 className="md:col-span-4 font-sans text-sm uppercase tracking-[0.35em] text-charcoal">{p.title}</h3>
                <p className="md:col-span-6 font-serif text-xl md:text-2xl text-taupe leading-snug">{p.text}</p>
              </div>
            </Reveal>
          ))}
          <div className="border-t border-stone/80" aria-hidden="true" />
        </div>
      </div>
    </section>

    <section data-testid="regulate-cta" className="bg-cream">
      <div className="mx-auto max-w-3xl px-6 md:px-10 py-28 md:py-36 text-center">
        <Reveal>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.15]">
            Come back to your body.
          </h2>
          <p className="mt-7 text-base md:text-lg leading-relaxed text-taupe max-w-xl mx-auto">
            Learn to live with more safety, steadiness, and choice.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-11 flex flex-col sm:flex-row justify-center gap-4">
            <AmazonButton testid="regulate-cta-amazon-button" />
            <Link
              to="/begin-here"
              data-testid="regulate-cta-begin-button"
              className="text-center border border-stone bg-white/50 text-charcoal text-xs uppercase tracking-[0.25em] px-10 py-4 hover:border-teal hover:text-deepteal transition-colors duration-500"
            >
              Begin with a Free Practice
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default RegulatePage;
